/**
 * Data transformation utilities for converting API responses to expected frontend formats
 */

import type { InsulinMeasurement, ECGFeatures, MealImage } from "../types/api";

// ============================================================================
// Insulin Data Transformers
// ============================================================================

interface APIInsulinMeasurement {
  id: string;
  patientId: string;
  timestamp: string;
  type: "basal" | "bolus" | "correction";
  units: number;
  notes?: string;
  createdAt: string;
}

/**
 * Transform API insulin measurement to frontend format
 */
export function transformInsulinMeasurement(
  apiMeasurement: APIInsulinMeasurement,
): InsulinMeasurement {
  // Map API types to frontend types
  const typeMapping: Record<string, InsulinMeasurement["type"]> = {
    basal: "long-acting",
    bolus: "rapid",
    correction: "rapid",
  };

  return {
    id: apiMeasurement.id,
    patientId: apiMeasurement.patientId,
    timestamp: apiMeasurement.timestamp,
    recordedAt: apiMeasurement.timestamp, // Use timestamp as recordedAt
    units: apiMeasurement.units,
    type: typeMapping[apiMeasurement.type] || "other",
    fastInsulinUnits:
      apiMeasurement.type === "bolus" ? apiMeasurement.units : undefined,
    slowInsulinUnits:
      apiMeasurement.type === "basal" ? apiMeasurement.units : undefined,
    notes: apiMeasurement.notes,
    createdAt: apiMeasurement.createdAt,
  };
}

/**
 * Transform array of API insulin measurements
 */
export function transformInsulinMeasurements(
  apiMeasurements: APIInsulinMeasurement[],
): InsulinMeasurement[] {
  return apiMeasurements.map(transformInsulinMeasurement);
}

// ============================================================================
// ECG Data Transformers
// ============================================================================

interface APIECGFeature {
  id: string;
  patientId: string;
  timestamp: string;
  heartRate: number;
  hrv?: number;
  rhythmClassification?: string;
  confidence?: number;
  notes?: string;
  createdAt?: string;
}

/**
 * Transform API ECG feature to frontend format
 */
export function transformECGFeature(apiFeature: APIECGFeature): ECGFeatures {
  // Map rhythm classifications
  const rhythmMapping: Record<
    string,
    ECGFeatures["waveformFeatures"]["rhythmClassification"]
  > = {
    normal: "normal",
    arrhythmia: "arrhythmia",
    bradycardia: "uncertain",
    tachycardia: "uncertain",
    irregular: "uncertain",
  };

  const heartRate = apiFeature.heartRate;
  const hrv = apiFeature.hrv || 45;

  return {
    id: apiFeature.id,
    patientId: apiFeature.patientId,
    timestamp: apiFeature.timestamp,
    recordingStart: apiFeature.timestamp,
    recordingEnd: apiFeature.timestamp,
    heartRate,
    rrIntervals: [], // Generate mock RR intervals based on heart rate
    hrvFeatures: {
      heartRateVariability: hrv,
      rmssd: Math.random() * 50 + 20, // Mock RMSSD 20-70
      pnn50: Math.random() * 10, // Mock PNN50 0-10
      sdnn: Math.random() * 30 + 40, // Mock SDNN 40-70
    },
    waveformFeatures: {
      rhythmClassification:
        rhythmMapping[apiFeature.rhythmClassification || "normal"] || "normal",
      qrsComplexCount: Math.floor(Math.random() * 100) + 50, // Mock QRS count
      averageRRInterval: 60000 / heartRate, // Calculate from HR
      prInterval: Math.random() * 50 + 120, // Mock PR interval 120-170ms
      qrsDuration: Math.random() * 40 + 80, // Mock QRS duration 80-120ms
      qtInterval: Math.random() * 100 + 350, // Mock QT interval 350-450ms
    },
    createdAt: apiFeature.createdAt || apiFeature.timestamp,
  };
}

/**
 * Transform array of API ECG features
 */
export function transformECGFeatures(
  apiFeatures: APIECGFeature[],
): ECGFeatures[] {
  return apiFeatures.map(transformECGFeature);
}

// ============================================================================
// Meal Image Transformers
// ============================================================================

interface APIMealImage {
  id: string;
  patientId: string;
  timestamp: string;
  imageUrl: string;
  mealType: string;
  description?: string;
  carbohydratesGrams?: number;
  calories?: number;
  createdAt: string;
}

/**
 * Transform API meal image to frontend format
 */
export function transformMealImage(apiImage: APIMealImage): MealImage {
  // Extract file format from URL or use default
  const format = apiImage.imageUrl.split(".").pop()?.toLowerCase() || "jpg";

  return {
    id: apiImage.id,
    patientId: apiImage.patientId,
    mealId: apiImage.mealType,
    timestamp: apiImage.timestamp,
    imageBase64: apiImage.imageUrl, // For now, use URL as base64 placeholder
    metadata: {
      format,
      size: 1024 * 500, // Mock 500KB average size
      width: 800, // Mock dimensions
      height: 600,
      description: apiImage.description,
      carbohydrates: apiImage.carbohydratesGrams,
      calories: apiImage.calories,
    },
    createdAt: apiImage.createdAt,
  };
}

/**
 * Transform array of API meal images
 */
export function transformMealImages(apiImages: APIMealImage[]): MealImage[] {
  return apiImages.map(transformMealImage);
}

// ============================================================================
// Response Transformers
// ============================================================================

interface APIResponse<T> {
  success: boolean;
  data?: T[];
  count?: number;
  error?: string;
  message?: string;
}

interface FrontendResponse<T> {
  data: T[];
  metadata?: {
    count: number;
    [key: string]: any;
  };
}

/**
 * Transform API response format to frontend expected format
 */
export function transformAPIResponse<T>(
  apiResponse: APIResponse<T>,
): FrontendResponse<T> {
  return {
    data: apiResponse.data || [],
    metadata: {
      count: apiResponse.count || apiResponse.data?.length || 0,
    },
  };
}

// ============================================================================
// Combined Transformers for Hooks
// ============================================================================

/**
 * Transform insulin API response for useInsulinData hook
 */
export function transformInsulinResponse(
  apiResponse: APIResponse<APIInsulinMeasurement>,
) {
  const transformed = transformAPIResponse(apiResponse);
  return {
    ...transformed,
    data: transformInsulinMeasurements(apiResponse.data || []),
  };
}

/**
 * Transform ECG API response for useECGFeatures hook
 */
export function transformECGResponse(apiResponse: APIResponse<APIECGFeature>) {
  const transformed = transformAPIResponse(apiResponse);
  return {
    ...transformed,
    data: transformECGFeatures(apiResponse.data || []),
  };
}

/**
 * Transform meal images API response for useMealImages hook
 */
export function transformMealImagesResponse(
  apiResponse: APIResponse<APIMealImage>,
) {
  const transformed = transformAPIResponse(apiResponse);
  return {
    ...transformed,
    data: transformMealImages(apiResponse.data || []),
  };
}
